<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="flex items-center justify-center min-h-screen text-center">
      <!-- Background overlay -->
      <div class="fixed inset-0 transition-opacity" @click="close">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- Modal panel -->
      <div class="bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg w-full">
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="flex justify-between items-start">
            <div>
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                Document Types Status
              </h3>
              <p class="mt-1 text-sm text-gray-500">
                Overview of required document types and their status
              </p>
            </div>

            <button @click="close" class="text-gray-400 hover:text-gray-500">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="mt-4">
            <div class="bg-gray-50 p-4 rounded-md">
              <div class="flex mb-2">
                <span class="text-sm font-medium text-gray-700 flex-1">Document Type</span>
                <span class="text-sm font-medium text-gray-700 w-24 text-center mr-6">Status</span>
              </div>
              
              <div class="space-y-2 max-h-[40vh] overflow-y-auto">
                <div 
                  v-for="(status, type) in documentTypesStatus" 
                  :key="type"
                  class="flex items-center p-2 rounded-md"
                  :class="status ? 'bg-green-50' : 'bg-red-50'"
                >
                  <span class="text-sm flex-1">{{ type }}</span>
                  <span 
                    class="px-2 py-1 text-xs font-medium rounded-full w-24 text-center"
                    :class="status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                  >
                    {{ status ? 'Available' : 'Missing' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Merged Documents List -->
          <div v-if="mergedDocuments && mergedDocuments.length > 0" class="mt-6">
            <h4 class="text-sm font-medium text-gray-700 mb-2">Merged Documents</h4>
            <div class="bg-gray-50 p-4 rounded-md">
              <div class="space-y-2 max-h-[30vh] overflow-y-auto">
                <div 
                  v-for="doc in mergedDocuments" 
                  :key="doc.id"
                  class="flex items-center justify-between p-2 rounded-md bg-white border border-gray-200"
                >
                  <div class="flex-1 mr-2">
                    <div class="text-sm font-medium">{{ doc.name }}</div>
                    <div class="text-xs text-gray-500">{{ formatDate(doc.timestamp) }}</div>
                  </div>
                  <div class="flex items-center">
                    <button
                      @click="downloadDocument(doc)"
                      class="px-2 py-1 text-xs bg-primary text-white rounded hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 mr-2"
                    >
                      Download
                    </button>
                    <button
                      @click="deleteDocument(doc)"
                      class="p-1 text-xs text-red-600 rounded hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                      title="Delete document"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="bg-gray-50 px-4 py-3 sm:px-6 flex justify-between">
          <button
            v-if="showMergeAnywayButton"
            @click="mergeAnyway"
            class="inline-flex justify-center rounded-md border border-primary shadow-sm px-4 py-2 bg-primary text-white text-base font-medium hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:text-sm"
          >
            Merge Anyway
          </button>
          <div v-else></div> <!-- Spacer wenn kein Button angezeigt wird -->
          <button
            @click="close"
            class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

export default defineComponent({
  name: 'DocumentTypesInfoModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    documentTypesStatus: {
      type: Object as PropType<Record<string, boolean>>,
      required: true
    },
    showMergeAnywayButton: {
      type: Boolean,
      default: false
    },
    mergedDocuments: {
      type: Array as PropType<any[]>,
      default: () => []
    }
  },
  emits: ['close', 'merge-anyway', 'download-document', 'delete-document'],
  setup(props, { emit }) {
    const close = () => {
      emit('close');
    };

    const mergeAnyway = () => {
      emit('merge-anyway');
    };
    
    const downloadDocument = (document: any) => {
      emit('download-document', document);
    };
    
    const deleteDocument = (document: any) => {
      emit('delete-document', document);
    };
    
    const formatDate = (timestamp: number): string => {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    return {
      close,
      mergeAnyway,
      downloadDocument,
      deleteDocument,
      formatDate
    };
  }
});
</script>
